/**
 * Role and module awareness for the notification centre.
 *
 * A module section is rendered only when the acting user can actually reach
 * that module. Two independent gates, both of which must pass:
 *
 *  1. **Per-user site permission** — the `UserSitePermission` flag the backend
 *     audience rules read (`can_purchasing`, `can_quality`) at any linked,
 *     active site. A user with no purchasing access gets no Purchasing section
 *     at all, empty or otherwise. Documents & Compliance has no site flag: it
 *     follows the Documents menu, the same thing that grants the page.
 *  2. **Tenant module switch** — the `MenuVisibility` keys for the module. If
 *     the tenant has switched Quality off, nobody sees a Quality section.
 *
 * Deliberate exemptions, matching the conventions already in
 * `UserPermissionsContext.hasModuleAccess`:
 *  - a user with no linked site permissions at all (supplier users, users
 *    predating the site-permission model) is not gated by rule 1;
 *  - a flag the API does not serialise is treated as ungated rather than as
 *    denied, so a narrower payload can never silently hide a module;
 *  - Helix staff and superusers see every module;
 *  - the System domain is never hidden — announcements and account notices
 *    must reach everyone.
 *
 * @link domainMeta for the per-domain gate declarations.
 */
import { useCallback, useMemo } from 'react';

import { type NotificationDomain, type UserSitePermission } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuSettings, type MenuVisibility } from '../../contexts/MenuSettingsContext';
import { useUserPermissions } from '../../contexts/UserPermissionsContext';
import { usePortalMode } from '../../hooks/usePortalMode';
import {
  NOTIFICATION_DOMAINS,
  NOTIFICATION_DOMAIN_META,
  isNotificationDomain,
  type NotificationModuleFlag,
} from './domainMeta';

export interface NotificationModuleAccess {
  /** True while permissions or tenant menu settings are still loading. */
  isLoading: boolean;
  /** Domains this user may see, in fixed render order. */
  visibleDomains: NotificationDomain[];
  /** True when a section for this domain should be rendered. */
  canSeeDomain: (domain: string) => boolean;
}

/**
 * Reads one site-permission flag across the user's linked sites.
 *
 * @returns true when at least one linked site grants it, or when the flag is
 *   absent from every row (the API did not serialise it — not a denial).
 */
function hasSiteFlag(
  linkedSites: UserSitePermission[],
  flag: NotificationModuleFlag
): boolean {
  if (!flag) return true;
  if (linkedSites.length === 0) return true;

  let sawFlag = false;
  let granted = false;
  linkedSites.forEach((permission) => {
    const row = permission as unknown as Record<string, boolean | undefined>;
    const value = row[flag];
    if (value === undefined) return;
    sawFlag = true;
    if (value) granted = true;
  });
  return sawFlag ? granted : true;
}

/**
 * Domain visibility for the current user.
 *
 * @returns loading state, the visible domain list and a per-domain predicate
 *
 * @example
 * const { canSeeDomain } = useNotificationModuleAccess();
 * const sections = groups.filter((group) => canSeeDomain(group.domain));
 */
export function useNotificationModuleAccess(): NotificationModuleAccess {
  const { user } = useAuth();
  const { sitePermissions, isLoading: permissionsLoading } = useUserPermissions();
  const { isMenuItemVisible, isLoading: menuLoading } = useMenuSettings();
  const { isSupplier, isHelix } = usePortalMode();

  const linkedSites = useMemo(
    () => sitePermissions.filter((permission) => permission.is_linked && permission.is_active),
    [sitePermissions]
  );

  const seesEverything = Boolean(isHelix || user?.is_superuser);

  const canSeeDomain = useCallback(
    (domain: string): boolean => {
      if (!isNotificationDomain(domain)) return true;
      const meta = NOTIFICATION_DOMAIN_META[domain];
      if (meta.alwaysVisible || seesEverything) return true;

      if (!hasSiteFlag(linkedSites, meta.sitePermissionFlag)) return false;

      // Onboarding is the supplier's own journey — never gated by the
      // customer-side menu switch for supplier users.
      if (domain === 'onboarding' && isSupplier) return true;

      if (meta.menuKeys.length === 0) return true;
      return meta.menuKeys.some((key) => isMenuItemVisible(key as keyof MenuVisibility));
    },
    [isMenuItemVisible, isSupplier, linkedSites, seesEverything]
  );

  const visibleDomains = useMemo(
    () => NOTIFICATION_DOMAINS.filter((domain) => canSeeDomain(domain)),
    [canSeeDomain]
  );

  return {
    isLoading: permissionsLoading || menuLoading,
    visibleDomains,
    canSeeDomain,
  };
}
